import { CheckCircle } from 'lucide-react';
import React, { useRef, useState, useMemo } from 'react';
import { saveEmailLayout } from './action';

type Speaker = {
  name: string;
  bio: string;
  image_url: string;
  organization: string;
  order: number;
};

type CallToAction = {
  text: string;
  url: string;
  order: number;
};

type Section = {
  title: string;
  body_text: string;
  order: number;
  height?: number;
};

type OrderedField = {
  text: string;
  order: number;
};

type EventData = {
  heading: OrderedField;
  subheading: OrderedField;
  event_date: OrderedField;
  event_time: OrderedField;
  location: OrderedField;
  description: OrderedField;
  speakers: Speaker[];
  call_to_action: CallToAction[];
  section: Section[];
};

interface EventWebinarInvitationProps {
  jsonData: EventData;
  threadId: string;
  userId: string;
}

const EventWebinarLayout: React.FC<EventWebinarInvitationProps> = ({ jsonData, threadId, userId }) => {
  const emailLayoutRef = useRef<HTMLDivElement | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to safely extract text
  const getText = (value: any): string => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object' && 'text' in value) return value.text;
    return String(value || '');
  };

  // Create ordered components array
  const orderedComponents = useMemo(() => {
    const components: Array<{ order: number; type: string; data?: any }> = [];

    // Add all fields with order property
    if (jsonData.heading?.order !== undefined) {
      components.push({ order: jsonData.heading.order, type: 'heading', data: jsonData.heading });
    }
    if (jsonData.subheading?.order !== undefined) {
      components.push({ order: jsonData.subheading.order, type: 'subheading', data: jsonData.subheading });
    }
    if (jsonData.event_date?.order !== undefined) {
      components.push({ order: jsonData.event_date.order, type: 'event_date', data: jsonData.event_date });
    }
    if (jsonData.event_time?.order !== undefined) {
      components.push({ order: jsonData.event_time.order, type: 'event_time', data: jsonData.event_time });
    }
    if (jsonData.location?.order !== undefined) {
      components.push({ order: jsonData.location.order, type: 'location', data: jsonData.location });
    }
    if (jsonData.description?.order !== undefined) {
      components.push({ order: jsonData.description.order, type: 'description', data: jsonData.description });
    }

    // Add call_to_action items
    if (Array.isArray(jsonData.call_to_action)) {
      jsonData.call_to_action.forEach((cta) => {
        if (cta.order !== undefined) {
          components.push({ order: cta.order, type: 'call_to_action', data: cta });
        }
      });
    }

    // Add section items
    if (Array.isArray(jsonData.section)) {
      jsonData.section.forEach((sec) => {
        if (sec.order !== undefined) {
          components.push({ order: sec.order, type: 'section', data: sec });
        }
      });
    }

    // Add speakers
    if (Array.isArray(jsonData.speakers)) {
      jsonData.speakers.forEach((speaker) => {
        if (speaker.order !== undefined) {
          components.push({ order: speaker.order, type: 'speaker', data: speaker });
        }
      });
    }

    // Sort by order
    return components.sort((a, b) => a.order - b.order);
  }, [jsonData]);

  const handleConfirmLayout = async () => {
    setIsSubmitting(true);
    if (!emailLayoutRef.current) return;

    const rawHtml = emailLayoutRef.current.innerHTML;

    const payload = {
      raw_html: rawHtml,
    };

    try {
      const ans = await saveEmailLayout(userId, threadId, payload);
      if (ans?.success === true) {
      setIsSuccess(true);
      } else {
        setError("Failed to save data");
      }
    } catch (error) {
      console.error("Failed to save email layout", error);
      setError("Failed to save data");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Common table style to remove all borders
  const tableStyle = {
    borderCollapse: 'collapse' as const,
    border: '0',
    borderStyle: 'none',
    borderWidth: '0'
  };

  const cellStyle = {
    border: '0',
    borderStyle: 'none',
    borderWidth: '0'
  };

  // Group speakers for rendering
  const groupedSpeakers = useMemo(() => {
    const speakers = orderedComponents.filter(c => c.type === 'speaker').map(c => c.data);
    const grouped: Speaker[][] = [];
    for (let i = 0; i < speakers.length; i += 2) {
      grouped.push(speakers.slice(i, i + 2));
    }
    return grouped;
  }, [orderedComponents]);

  // Render component based on type
  const renderComponent = (component: { order: number; type: string; data?: any }, index: number) => {
    switch (component.type) {
      case 'heading':
        return (
          <tr key={`heading-${index}`}>
            <td style={{ ...cellStyle, padding: '0 20px 8px 20px' }}>
              <h1 style={{
                fontSize: '24px',
                fontWeight: 'bold',
                marginBottom: '8px',
                marginTop: '0',
                fontFamily: 'Arial, Helvetica, sans-serif',
                color: '#000c36'
              }}>
                {getText(component.data)}
              </h1>
            </td>
          </tr>
        );

      case 'subheading':
        return (
          <tr key={`subheading-${index}`}>
            <td style={{ ...cellStyle, padding: '0 20px 8px 20px' }}>
              <h2 style={{
                fontSize: '18px',
                color: '#374151',
                fontWeight: '400',
                margin: '0',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                {getText(component.data)}
              </h2>
            </td>
          </tr>
        );

      case 'description':
        return (
          <tr key={`description-${index}`}>
            <td style={{
              ...cellStyle,
              color: '#374151',
              backgroundColor: '#dbeafe',
              padding: '16px 20px'
            }}>
              <p style={{
                fontSize: '16px',
                margin: '0',
                fontFamily: 'Arial, Helvetica, sans-serif',
                lineHeight: '1.5'
              }}>
                {getText(component.data)}
              </p>
            </td>
          </tr>
        );

      case 'location':
      case 'event_date':
      case 'event_time':
        return (
          <tr key={`${component.type}-${index}`}>
            <td style={{
              ...cellStyle,
              color: '#374151',
              backgroundColor: '#dbeafe',
              padding: '8px 20px'
            }}>
              <p style={{
                fontWeight: '600',
                margin: '0 0 4px 0',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                {component.type === 'location' ? 'Location:' : 
                 component.type === 'event_date' ? 'Date:' : 'Time:'}
              </p>
              <p style={{
                margin: '0',
                fontFamily: 'Arial, Helvetica, sans-serif'
              }}>
                {getText(component.data)}
              </p>
            </td>
          </tr>
        );

      case 'call_to_action':
        return (
          <tr key={`cta-${index}`}>
            <td style={{ ...cellStyle, padding: '12px 20px' }}>
              <table cellPadding="0" cellSpacing="0" border={0} style={tableStyle}>
                <tbody>
                  <tr>
                    <td style={{
                      ...cellStyle,
                      fontSize: '14px',
                      padding: '8px 12px',
                      backgroundColor: '#2563eb',
                      borderRadius: '0',
                      display: 'inline-block'
                    }}>
                      <a
                        href={getText(component.data.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: 'white',
                          fontWeight: '600',
                          textDecoration: 'none',
                          fontFamily: 'Arial, Helvetica, sans-serif',
                          display: 'inline-block'
                        }}
                      >
                        {getText(component.data.text)}
                      </a>
                    </td>
                  </tr>
                </tbody>
              </table>
            </td>
          </tr>
        );

      case 'section':
        return (
          <React.Fragment key={`section-${index}`}>
            <tr>
              <td style={{ ...cellStyle, padding: '16px 20px' }}>
                <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                  <tbody>
                    <tr>
                      <td style={{
                        ...cellStyle,
                        backgroundColor: '#e5e7eb',
                        height: '2px',
                        fontSize: '0',
                        lineHeight: '0'
                      }}>
                        &nbsp;
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
            <tr>
              <td style={{ ...cellStyle, padding: '16px 20px' }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  margin: '0 0 8px 0',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  color: '#000c36'
                }}>
                  {getText(component.data.title)}
                </h3>
                <p style={{
                  color: '#374151',
                  whiteSpace: 'pre-line',
                  margin: '0',
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  lineHeight: '1.5'
                }}>
                  {getText(component.data.body_text)}
                </p>
              </td>
            </tr>
          </React.Fragment>
        );

      case 'speaker':
        // Speakers are rendered separately in grouped format
        return null;

      default:
        return null;
    }
  };

  return (
    <div style={{ maxWidth: '36rem' }}>
      {isSuccess ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'start',
          padding: '65px 24px',
          border: '1px solid #d1d5db',
          borderRadius: '12px',
          height: '86vh',
        }}>
          <CheckCircle style={{ width: '25px', height: '25px', color: '#16a34a', marginBottom: '16px' }} />
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
            Email Layout saved successfully
          </h2>
          <p style={{ fontSize: '14px', color: '#4b5563', marginTop: '8px', textAlign: 'center' }}>
            Thank you! Your information has been saved successfully.
          </p>
        </div>
      ) : (
        <>
          <div ref={emailLayoutRef} style={{ height: '86vh', overflowY: 'auto' }}>
            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{
              ...tableStyle,
              background: '#eff6ff',
              padding: '30px',
              margin: '0',
              fontFamily: 'Arial, Helvetica, sans-serif'
            }}>
              <tbody>
                <tr>
                  <td align="center" style={cellStyle}>
                    <table cellPadding="0" cellSpacing="0" border={0} width="700" style={{
                      ...tableStyle,
                      maxWidth: '700px',
                      margin: '0 auto',
                      fontFamily: 'Arial, Helvetica, sans-serif'
                    }}>
                      <tbody>
                        {/* Header */}
                        <tr>
                          <td style={{
                            ...cellStyle,
                            backgroundColor: '#FFFFFF',
                            padding: '15px',
                            fontFamily: 'Arial, Helvetica, sans-serif'
                          }}>
                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                              <tbody>
                                <tr>
                                  <td align="left" valign="middle" style={cellStyle}>
                                    <a href="https://www.broadridge.com/" target="_blank" rel="noopener noreferrer">
                                      <img src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg" 
                                           alt="Broadridge Logo" 
                                           width="112" 
                                           height="auto" 
                                           style={{ display: 'block', border: '0' }} />
                                    </a>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>

                        {/* Main Content - Dynamic Order */}
                        <tr>
                          <td style={{ ...cellStyle, backgroundColor: 'white', padding: '8px 0' }}>
                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                              <tbody>
                                {orderedComponents.map((component, index) => 
                                  renderComponent(component, index)
                                )}

                                {/* Speakers Section - Rendered after dynamic content */}
                                {groupedSpeakers.length > 0 && (
                                  <>
                                    <tr>
                                      <td style={{ ...cellStyle, padding: '16px 20px' }}>
                                        <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                                          <tbody>
                                            <tr>
                                              <td style={{
                                                ...cellStyle,
                                                backgroundColor: '#e5e7eb',
                                                height: '1px',
                                                fontSize: '0',
                                                lineHeight: '0'
                                              }}>
                                                &nbsp;
                                              </td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                    <tr>
                                      <td style={{ ...cellStyle, padding: '0 20px 16px 20px' }}>
                                        <h3 style={{
                                          fontSize: '18px',
                                          fontWeight: '600',
                                          marginBottom: '16px',
                                          marginTop: '0',
                                          fontFamily: 'Arial, Helvetica, sans-serif',
                                          color: '#000c36'
                                        }}>
                                          Speakers
                                        </h3>
                                        <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                                          <tbody>
                                            {groupedSpeakers.map((speakerPair, pairIndex) => (
                                              <tr key={`speaker-row-${pairIndex}`}>
                                                {speakerPair.map((speaker, speakerIndex) => (
                                                  <td key={`speaker-${pairIndex}-${speakerIndex}`} width="50%" valign="top" 
                                                      style={{ ...cellStyle, padding: speakerIndex === 0 ? '0 12px 12px 0' : '0 0 12px 12px' }}>
                                                    <table cellPadding="0" cellSpacing="0" border={0} style={tableStyle}>
                                                      <tbody>
                                                        <tr>
                                                          <td style={cellStyle}>
                                                            <table cellPadding="0" cellSpacing="0" border={0} style={tableStyle}>
                                                              <tbody>
                                                                <tr>
                                                                  <td style={{
                                                                    ...cellStyle,
                                                                    overflow: 'hidden',
                                                                    paddingBottom: '8px'
                                                                  }}>
                                                                    {getText(speaker.image_url) ? (
                                                                      <img
                                                                        src={getText(speaker.image_url)}
                                                                        alt={getText(speaker.name)}
                                                                        width="65"
                                                                        height="65"
                                                                        style={{
                                                                          display: 'block',
                                                                          border: '0',
                                                                          borderRadius: '50%',
                                                                          width: '125px',
                                                                          height: '125px',
                                                                          backgroundColor: '#e5e7eb',
                                                                        }}
                                                                      />
                                                                    ) : (
                                                                      <div style={{
                                                                        width: '65px',
                                                                        height: '65px',
                                                                        backgroundColor: '#e5e7eb',
                                                                        borderRadius: '50%'
                                                                      }}>&nbsp;</div>
                                                                    )}
                                                                  </td>
                                                                </tr>
                                                                <tr>
                                                                  <td style={{ ...cellStyle, paddingTop: '8px' }}>
                                                                    <p style={{
                                                                      fontWeight: '600',
                                                                      margin: '0 0 4px 0',
                                                                      fontSize: '16px',
                                                                      fontFamily: 'Arial, Helvetica, sans-serif',
                                                                      color: '#000c36'
                                                                    }}>
                                                                      {getText(speaker.name)}
                                                                    </p>
                                                                    <p style={{
                                                                      fontSize: '12px',
                                                                      color: '#6b7280',
                                                                      margin: '0',
                                                                      fontFamily: 'Arial, Helvetica, sans-serif'
                                                                    }}>
                                                                      {getText(speaker.organization)}
                                                                    </p>
                                                                  </td>
                                                                </tr>
                                                              </tbody>
                                                            </table>
                                                          </td>
                                                        </tr>
                                                      </tbody>
                                                    </table>
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </td>
                                    </tr>
                                  </>
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>

                        {/* Footer */}
                        <tr>
                          <td style={{ ...cellStyle, backgroundColor: '#f0f0f0', padding: '30px 20px' }}>
                            <p style={{
                              fontSize: '14px',
                              lineHeight: '20px',
                              marginBottom: '20px',
                              marginTop: '0',
                              color: '#3c3c3c',
                              fontFamily: 'Arial, Helvetica, sans-serif'
                            }}>
                              Broadridge Financial Solutions (NYSE: BR) is a global technology leader with
                              trusted expertise and transformative technology, helping clients and the financial
                              services industry operate, innovate, and grow. We power investing, governance,
                              and communications for our clients - driving operational resiliency, elevating
                              business performance, and transforming investor experiences. For more information,
                              please visit <a href="https://broadridge.com" style={{ color: '#2662fc' }}>Broadridge.com</a>.
                            </p>
                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                              <tbody>
                                <tr>
                                  <td align="left" valign="middle" style={cellStyle}>
                                    <a href="https://www.broadridge.com/" target="_blank" rel="noopener noreferrer">
                                      <img
                                        src="https://www.broadridge.com/_Static-Assets/images/brodridgeLogo.svg"
                                        alt="Broadridge"
                                        height="30"
                                        style={{ display: 'block', border: '0' }}
                                      />
                                    </a>
                                  </td>
                                  <td align="right" valign="middle" style={{
                                    ...cellStyle,
                                    fontSize: '22px',
                                    fontWeight: '400',
                                    color: '#000c36',
                                    fontFamily: 'Arial, Helvetica, sans-serif'
                                  }}>
                                    Ready for Next
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>

                        {/* Reason for receiving email */}
                        <tr>
                          <td style={{ ...cellStyle, backgroundColor: '#fff', padding: '20px' }}>
                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                              <tbody>
                                <tr>
                                  <td width="50%" valign="top" style={{
                                    ...cellStyle,
                                    fontSize: '12px',
                                    color: '#999999',
                                    lineHeight: '16px',
                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                    paddingRight: '10px'
                                  }}>
                                    You are receiving this email because <strong>anusha.kakaraparthi@broadridge.com</strong> is signed up
                                    to receive Broadridge communications.
                                  </td>
                                  <td width="50%" valign="top" style={{
                                    ...cellStyle,
                                    fontSize: '12px',
                                    color: '#999999',
                                    lineHeight: '16px',
                                    fontFamily: 'Arial, Helvetica, sans-serif',
                                    paddingLeft: '10px'
                                  }}>
                                    Delivered by Broadridge Financial Solutions, Inc., 5 Dakota Drive, Suite 300,
                                    Lake Success, NY 11042, USA
                                  </td>
                                </tr>
                              </tbody>
                            </table>

                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={{ ...tableStyle, marginTop: '20px' }}>
                              <tbody>
                                <tr>
                                  <td style={{
                                    ...cellStyle,
                                    fontSize: '12px',
                                    color: '#999999',
                                    lineHeight: '16px',
                                    paddingBottom: '20px',
                                    fontFamily: 'Arial, Helvetica, sans-serif'
                                  }}>
                                    This message and any attachments are intended only for the use of the addressee and
                                    may contain information that is privileged and confidential. If the reader of the message
                                    is not the intended recipient or an authorized representative of the intended recipient,
                                    you are hereby notified that any dissemination of this communication is strictly prohibited.
                                    If you have received this communication in error, please notify us immediately by e-mail
                                    and delete the message and any attachments from your system.
                                  </td>
                                </tr>
                              </tbody>
                            </table>

                            <table cellPadding="0" cellSpacing="0" border={0} width="100%" style={tableStyle}>
                              <tbody>
                                <tr>
                                  <td valign="middle" style={{
                                    ...cellStyle,
                                    fontSize: '12px',
                                    color: '#777',
                                    fontFamily: 'Arial, Helvetica, sans-serif'
                                  }}>
                                    © Copyright 2025, Broadridge Financial Solutions, Inc.<br />
                                    <a target="_blank" rel="noopener noreferrer" href="https://www.broadridge.com/legal/privacy-statement-english" style={{ color: '#1a0dab' }}>
                                      Privacy Statement
                                    </a>
                                  </td>
                                  <td align="right" valign="middle" style={cellStyle}>
                                    <table cellPadding="0" cellSpacing="0" border={0} style={tableStyle}>
                                      <tbody>
                                        <tr>
                                          <td style={{ ...cellStyle, padding: '0 7px' }}>
                                            <a target="_blank" rel="noopener noreferrer" href="https://www.linkedin.com/company/broadridge-financial-solutions/">
                                              <img src="https://i.ibb.co/35CWCWMX/br-3.jpg" width="24" height="24" alt="LinkedIn" style={{ display: 'block', border: '0' }} />
                                            </a>
                                          </td>
                                          <td style={{ ...cellStyle, padding: '0 7px' }}>
                                            <a target="_blank" rel="noopener noreferrer" href="https://instagram.com/broadridge">
                                              <img src="https://i.ibb.co/0R8gGrZv/br-4.jpg" width="24" height="24" alt="Instagram" style={{ display: 'block', border: '0' }} />
                                            </a>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%', marginTop: '8px' }}>
            <button
              onClick={handleConfirmLayout}
              disabled={isSubmitting}
              style={{
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                opacity: isSubmitting ? 0.5 : 1
              }}
              className='rounded-xl  text-sm text-white inline-flex justify-center items-center px-[12px] py-[10px] bg-primary-900 font-medium'
            >
              {isSubmitting ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg style={{ animation: 'spin 1s linear infinite', height: '8px', width: '8px', marginRight: '8px' }} viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Confirming Email Layout
                </span>
              ) : (
                'Confirm Email Layout'
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default EventWebinarLayout;